import { ApiQuestionResponse, QuestionScore } from '~teacher/models/activities';

export interface StudentTotalScore {
  score: number;
  maxScore: number;
  percentage: number;
}

interface CalculateStudentTotalScoresParams {
  studentUserUuid: string;
  studentId: number;
  questionIds: number[];
  questionScores: Record<string, QuestionScore>;
  responses: Record<string, ApiQuestionResponse>;
}

/**
 * Calculates the total score for a student across all questions.
 * 
 * Question scores only contain manual scores from teacher grading.
 * Responses contain autograded scores (isCorrect) for MC/TF questions.
 * 
 * For each question: use manual score if available, otherwise use autograded isCorrect.
 */
export function calculateStudentTotalScore({
  studentUserUuid,
  studentId,
  questionIds,
  questionScores,
  responses,
}: CalculateStudentTotalScoresParams): StudentTotalScore {
  /* Build a map of responses by question ID for quick lookup 
   * This is used to calculate the student score from autograded responses
   */
  const responsesByQuestionId: Record<number, { isCorrect: boolean | null }> = {};
  Object.values(responses).forEach((response) => {
    if (response.userUuid === studentUserUuid) {
      responsesByQuestionId[response.questionId] = { isCorrect: response.isCorrect };
    }
  });

  /* Calculate student score by summing scores across all questions
   * For each question: use manual score if available, otherwise use autograded isCorrect
   */
  let score = 0;

  questionIds.forEach((questionId) => {
    // Check for manual grade override first
    const manualScore = questionScores[questionId]?.studentScores[studentId];
    if (manualScore !== undefined) {
      score += manualScore.score;
    } else {
      // Fall back to autograded response
      const response = responsesByQuestionId[questionId];
      if (response?.isCorrect === true) {
        score += 1;
      }
      // isCorrect === false or null means 0 points
    }
  });

  /* Max score = number of questions from the quiz
   * Since each question is worth 1 point
   */
  const maxScore = questionIds.length;
  const percentage = maxScore > 0 ? score / maxScore : 0;

  return { score, maxScore, percentage };
}
